import { useMemo, useState } from "react";
import { api } from "../../api/client.js";
import type {
  CreateEstateGraphFactInput,
  EstateGraphFactRevisionView,
  ThreatModelWorkspace,
} from "../../api/types.js";
import { InlineError } from "../../components/AsyncState.js";

type FactType = CreateEstateGraphFactInput["factType"];

export interface GraphFactDraft {
  factType: FactType;
  subjectId: string;
  nodeKind: "component" | "entrypoint" | "identity" | "data_store" | "queue" | "external_party";
  nodeName: string;
  edgeFromNodeId: string;
  edgeToNodeId: string;
  edgeKind: "auth" | "data" | "fund" | "secret" | "queue" | "webhook" | "mtls";
  crownNodeId: string;
  crownClassification:
    | "funds"
    | "customer_data"
    | "credentials"
    | "availability"
    | "regulated_data";
  crownSensitivity: "high" | "critical";
  requiredInvariantsText: string;
  sourceRef: string;
  rationale: string;
  ownerId: string;
}

const EMPTY_DRAFT: GraphFactDraft = {
  factType: "node",
  subjectId: "",
  nodeKind: "component",
  nodeName: "",
  edgeFromNodeId: "",
  edgeToNodeId: "",
  edgeKind: "data",
  crownNodeId: "",
  crownClassification: "availability",
  crownSensitivity: "high",
  requiredInvariantsText: "",
  sourceRef: "",
  rationale: "",
  ownerId: "",
};

function latestForDraft(
  revisions: readonly EstateGraphFactRevisionView[],
  draft: GraphFactDraft,
): EstateGraphFactRevisionView | null {
  return (
    revisions
      .filter(
        (revision) =>
          revision.factType === draft.factType && revision.subjectId === draft.subjectId.trim(),
      )
      .at(-1) ?? null
  );
}

export function graphFactInput(
  snapshotId: string,
  draft: GraphFactDraft,
  latest: EstateGraphFactRevisionView | null,
): CreateEstateGraphFactInput {
  const subjectId = draft.subjectId.trim();
  const decision = latest?.decision === "approved" ? "revoked" : "approved";
  let subject: CreateEstateGraphFactInput["subject"] = null;
  if (decision === "approved" && draft.factType === "node") {
    subject = { id: subjectId, kind: draft.nodeKind, name: draft.nodeName.trim() };
  } else if (decision === "approved" && draft.factType === "edge") {
    subject = {
      id: subjectId,
      fromNodeId: draft.edgeFromNodeId,
      toNodeId: draft.edgeToNodeId,
      kind: draft.edgeKind,
    };
  } else if (decision === "approved") {
    subject = {
      id: subjectId,
      nodeId: draft.crownNodeId,
      classification: draft.crownClassification,
      sensitivity: draft.crownSensitivity,
      requiredInvariants: [
        ...new Set(
          draft.requiredInvariantsText
            .split("\n")
            .map((value) => value.trim())
            .filter(Boolean),
        ),
      ],
      ingressNodeIds: [],
      egressNodeIds: [],
    };
  }
  return {
    snapshotId,
    factType: draft.factType,
    subjectId,
    expectedParentSha256: latest?.canonicalSha256 ?? null,
    decision,
    subject,
    sourceRef: draft.sourceRef.trim(),
    rationale: draft.rationale.trim(),
    ownerId: draft.ownerId.trim(),
  };
}

export function graphFactDraftError(
  draft: GraphFactDraft,
  latest: EstateGraphFactRevisionView | null,
): string | null {
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(draft.subjectId.trim())) {
    return "A stable fact ID is required.";
  }
  if (!draft.sourceRef.trim() || !draft.ownerId.trim() || draft.rationale.trim().length < 10) {
    return "Source reference, tenant owner, and a substantive rationale are required.";
  }
  if (latest?.decision === "approved") return null;
  if (draft.factType === "node" && !draft.nodeName.trim()) return "A node name is required.";
  if (
    draft.factType === "edge" &&
    (!draft.edgeFromNodeId || !draft.edgeToNodeId || draft.edgeFromNodeId === draft.edgeToNodeId)
  ) {
    return "A validated edge requires distinct known endpoints.";
  }
  if (
    draft.factType === "crown_jewel" &&
    (!draft.crownNodeId || !draft.requiredInvariantsText.trim())
  ) {
    return "A crown jewel requires a known node and at least one invariant.";
  }
  return null;
}

export function GovernedGraphFactEditor({
  workspace,
  onSaved,
}: {
  workspace: ThreatModelWorkspace;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const latest = useMemo(
    () => latestForDraft(workspace.factRevisions, draft),
    [workspace.factRevisions, draft],
  );
  const draftError = graphFactDraftError(draft, latest);
  const revoking = latest?.decision === "approved";

  async function save() {
    if (draftError) return;
    setSaving(true);
    setError(null);
    try {
      await api.createEstateGraphFact(
        graphFactInput(workspace.projection.graph.snapshotId, draft, latest),
      );
      setDraft(EMPTY_DRAFT);
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error("Graph fact revision failed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="panel-card" aria-labelledby="graph-fact-editor-heading">
      <h2 id="graph-fact-editor-heading">Governed graph facts</h2>
      <p>
        An owner or risk manager may explicitly approve a fact with attributable provenance. This
        changes graph scope only; it never changes finding truth or fabricates replay evidence.
      </p>
      <div className="threat-model-editor">
        <label className="form-field">
          <span>Fact type</span>
          <select
            value={draft.factType}
            onChange={(event) => setDraft({ ...draft, factType: event.target.value as FactType })}
          >
            <option value="node">Node</option>
            <option value="edge">Trust/data edge</option>
            <option value="crown_jewel">Crown jewel</option>
          </select>
        </label>
        <label className="form-field">
          <span>Stable fact ID</span>
          <input
            value={draft.subjectId}
            onChange={(event) => setDraft({ ...draft, subjectId: event.target.value })}
          />
        </label>

        {revoking ? (
          <div className="callout warning-callout">
            <div>
              <strong>This fact is currently approved.</strong>
              <p>
                The next append-only revision will revoke it from the current graph. Existing model
                revisions retain their prior graph hash and custody.
              </p>
            </div>
          </div>
        ) : draft.factType === "node" ? (
          <>
            <label className="form-field">
              <span>Node kind</span>
              <select
                value={draft.nodeKind}
                onChange={(event) =>
                  setDraft({ ...draft, nodeKind: event.target.value as GraphFactDraft["nodeKind"] })
                }
              >
                <option value="component">Component</option>
                <option value="entrypoint">Entrypoint</option>
                <option value="identity">Identity</option>
                <option value="data_store">Data store</option>
                <option value="queue">Queue</option>
                <option value="external_party">External party</option>
              </select>
            </label>
            <label className="form-field">
              <span>Node name</span>
              <input
                value={draft.nodeName}
                onChange={(event) => setDraft({ ...draft, nodeName: event.target.value })}
              />
            </label>
          </>
        ) : draft.factType === "edge" ? (
          <>
            <label className="form-field">
              <span>From node</span>
              <select
                value={draft.edgeFromNodeId}
                onChange={(event) => setDraft({ ...draft, edgeFromNodeId: event.target.value })}
              >
                <option value="">Select a node</option>
                {workspace.projection.graph.nodes.map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>To node</span>
              <select
                value={draft.edgeToNodeId}
                onChange={(event) => setDraft({ ...draft, edgeToNodeId: event.target.value })}
              >
                <option value="">Select a node</option>
                {workspace.projection.graph.nodes.map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>Edge kind</span>
              <select
                value={draft.edgeKind}
                onChange={(event) =>
                  setDraft({ ...draft, edgeKind: event.target.value as GraphFactDraft["edgeKind"] })
                }
              >
                <option value="auth">Authorization</option>
                <option value="data">Data</option>
                <option value="fund">Funds</option>
                <option value="secret">Secret</option>
                <option value="queue">Queue</option>
                <option value="webhook">Webhook</option>
                <option value="mtls">mTLS</option>
              </select>
            </label>
          </>
        ) : (
          <>
            <label className="form-field">
              <span>Crown-jewel node</span>
              <select
                value={draft.crownNodeId}
                onChange={(event) => setDraft({ ...draft, crownNodeId: event.target.value })}
              >
                <option value="">Select a node</option>
                {workspace.projection.graph.nodes.map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>Classification</span>
              <select
                value={draft.crownClassification}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    crownClassification: event.target
                      .value as GraphFactDraft["crownClassification"],
                  })
                }
              >
                <option value="funds">Funds</option>
                <option value="customer_data">Customer data</option>
                <option value="credentials">Credentials</option>
                <option value="availability">Availability</option>
                <option value="regulated_data">Regulated data</option>
              </select>
            </label>
            <label className="form-field">
              <span>Sensitivity</span>
              <select
                value={draft.crownSensitivity}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    crownSensitivity: event.target.value as GraphFactDraft["crownSensitivity"],
                  })
                }
              >
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </label>
            <label className="form-field">
              <span>Required invariants · one per line</span>
              <textarea
                value={draft.requiredInvariantsText}
                onChange={(event) =>
                  setDraft({ ...draft, requiredInvariantsText: event.target.value })
                }
              />
            </label>
          </>
        )}

        <label className="form-field">
          <span>Tenant owner principal ID</span>
          <input
            value={draft.ownerId}
            onChange={(event) => setDraft({ ...draft, ownerId: event.target.value })}
          />
        </label>
        <label className="form-field">
          <span>Source reference</span>
          <input
            value={draft.sourceRef}
            onChange={(event) => setDraft({ ...draft, sourceRef: event.target.value })}
          />
        </label>
        <label className="form-field">
          <span>Approval or revocation rationale</span>
          <textarea
            value={draft.rationale}
            onChange={(event) => setDraft({ ...draft, rationale: event.target.value })}
          />
        </label>
        {draftError && (draft.subjectId || draft.sourceRef || draft.rationale || draft.ownerId) ? (
          <p className="field-error">{draftError}</p>
        ) : null}
        {error ? <InlineError error={error} onRetry={() => void save()} /> : null}
        <button
          className={revoking ? "button button-danger" : "button button-primary"}
          type="button"
          disabled={saving || Boolean(draftError)}
          onClick={() => void save()}
        >
          {saving ? "Appending…" : revoking ? "Append revocation" : "Append approval"}
        </button>
      </div>
    </article>
  );
}
